import React, { useState, useEffect } from "react";
import { Form, Input, Switch, Button, Card, Upload, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import axios from "axios";
import { baseURL } from "./api";

const saveSettingsToStorage = (data) => {
  localStorage.setItem("billSettings", JSON.stringify(data));
};

const getSettingsFromStorage = () => {
  const data = localStorage.getItem("billSettings");
  return data ? JSON.parse(data) : null;
};

const BillConfig = () => {
  const [form] = Form.useForm();
  const [logoUrl, setLogoUrl] = useState("");
  const [logoDisplay, setLogoDisplay] = useState(true);

  useEffect(() => {
    const vendorId = localStorage.getItem("vendorId");
    if (!vendorId) {
      message.error("Vendor ID not found in local storage!");
      return;
    }

    const saved = getSettingsFromStorage();
    if (saved) {
      form.setFieldsValue(saved.fields);
      setLogoUrl(saved.logoUrl);
      setLogoDisplay(saved.display.logo);
    }

    const fetchVendorDetails = async () => {
      try {
        const res = await axios.get(`${baseURL}/getVendor/${vendorId}`);
        const vendor = res.data;

        form.setFieldsValue({
          vendorName: vendor.vendorName || "",
          display_vendorName: true,
          vendorAddress: vendor.vendorAddress || "",
          display_vendorAddress: true,
          city: vendor.city || "",
          display_city: true,
          state: vendor.state || "",
          display_state: true,
          gstIn: vendor.gstIn || "",
          display_gstIn: true,
          mobile: vendor.mobile || "",
          display_mobile: true,
          billWithoutGst: false, // default value
        });

        if (vendor.logoUrl) {
          setLogoUrl(vendor.logoUrl);
        }
        setLogoDisplay(true);
      } catch (err) {
        console.error("❌ Error fetching vendor details:", err);
        message.error("Failed to fetch vendor details.");
      }
    };

    fetchVendorDetails();
  }, [form]);

  const handleLogoUpload = (info) => {
    if (info.file.status === "done" || info.file.originFileObj) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoUrl(e.target.result);
      };
      reader.readAsDataURL(info.file.originFileObj);
    }
  };

  const onFinish = async (values) => {
  const vendorId = localStorage.getItem("vendorId");
  if (!vendorId) {
    message.error("Vendor ID not found in local storage!");
    return;
  }

  const displayConfig = {
    logo: logoDisplay,
    vendorName: values.display_vendorName,
    vendorAddress: values.display_vendorAddress,
    city: values.display_city,
    state: values.display_state,
    gstIn: values.display_gstIn,
    mobile: values.display_mobile,
    billWithoutGst: values.billWithoutGst,
  };

  const payload = {
    vendorId,        // ✅ include vendorId
    logoUrl,         // ✅ logo base64
    fields: values,  // ✅ all form values
    display: displayConfig, // ✅ display config
  };

  try {
    // 🔥 Call your backend API
    await axios.post(`${baseURL}/billSettings`, payload);

    // ✅ Optionally still save to local storage as a backup
    saveSettingsToStorage(payload);

    message.success("✅ Settings saved to backend successfully!");
  } catch (err) {
    console.error("❌ Error saving settings:", err);
    message.error("❌ Failed to save settings to backend!");
  }
};


  return (
    <div style={{ maxWidth: 1200, margin: "50px auto" }}>
      <Card title="Bill Configuration" bordered>
        <Form layout="vertical" form={form} onFinish={onFinish}>
          {/* Logo Upload */}
          <Form.Item label="Logo">
            <Upload
              listType="picture"
              maxCount={1}
              beforeUpload={() => false}
              onChange={handleLogoUpload}
            >
              <Button icon={<UploadOutlined />}>Upload Logo</Button>
            </Upload>
            {logoUrl && (
              <div style={{ marginTop: 10 }}>
                <img src={logoUrl} alt="Logo Preview" style={{ maxHeight: 60 }} />
              </div>
            )}
            <div style={{ marginTop: 10 }}>
              Display Logo: <Switch checked={logoDisplay} onChange={setLogoDisplay} />
            </div>
          </Form.Item>

          {/* Vendor Name */}
          <Form.Item label="Brand Name" name="vendorName">
            <Input placeholder="e.g. Gargi Foods" disabled />
          </Form.Item>
          <Form.Item label="Display Brand Name" name="display_vendorName" valuePropName="checked">
            <Switch />
          </Form.Item>

          {/* Address */}
          <Form.Item label="Address" name="vendorAddress">
            <Input placeholder="e.g. Shop No. 10, Main Street" disabled />
          </Form.Item>
          <Form.Item label="Display Address" name="display_vendorAddress" valuePropName="checked">
            <Switch />
          </Form.Item>

          {/* City */}
          <Form.Item label="City" name="city">
            <Input placeholder="e.g. Mumbai" disabled />
          </Form.Item>
          <Form.Item label="Display City" name="display_city" valuePropName="checked">
            <Switch />
          </Form.Item>

          {/* State */}
          <Form.Item label="State" name="state">
            <Input placeholder="e.g. Maharashtra" disabled />
          </Form.Item>
          <Form.Item label="Display State" name="display_state" valuePropName="checked">
            <Switch />
          </Form.Item>

          {/* GST */}
          <Form.Item label="GST Number" name="gstIn">
            <Input placeholder="e.g. 27XXXXXXXXX1Z5" disabled />
          </Form.Item>
          <Form.Item label="Display GST Number" name="display_gstIn" valuePropName="checked">
            <Switch />
          </Form.Item>

          {/* Contact */}
          <Form.Item label="Contact Number" name="mobile">
            <Input placeholder="e.g. 9876543210" disabled />
          </Form.Item>
          <Form.Item label="Display Contact" name="display_mobile" valuePropName="checked">
            <Switch />
          </Form.Item>

          {/* ✅ New Switch: Bill Without GST */}
          <Form.Item
            label="Bill Without GST"
            name="billWithoutGst"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Button type="primary" htmlType="submit" block>
            Save Configuration
          </Button>
        </Form>
      </Card>
    </div>
  );
};

export default BillConfig;
